import React, { useState } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { useLang } from '@/context/LangContext';
import { useTeams } from '@/context/TeamContext';
import { Shield, Plus, Pencil, Check, X, Trash2, Star, Image as ImageIcon } from 'lucide-react';
import { api } from '@/lib/api';

const CATEGORIES = [
  { id: 'male', labelKey: 'team_cat_male' },
  { id: 'female', labelKey: 'team_cat_female' },
  { id: 'mixed', labelKey: 'team_cat_mixed' },
];

function CategoryPicker({ value, onChange }) {
  const { t } = useLang();
  return (
    <div className="inline-flex border border-gold/30 rounded-md overflow-hidden">
      {CATEGORIES.map(c => (
        <button
          key={c.id}
          type="button"
          onClick={() => onChange(c.id)}
          data-testid={`team-category-${c.id}`}
          className={`px-3 py-1.5 text-xs font-h uppercase tracking-wider ${value === c.id ? 'bg-gold text-navy' : 'text-gold hover:bg-gold/10'}`}
        >
          {t(c.labelKey)}
        </button>
      ))}
    </div>
  );
}

export default function Teams() {
  const { t } = useLang();
  const { teams, activeTeamId, switchTeam, createTeam, renameTeam, setCategory, deleteTeam, loading } = useTeams();
  const [newName, setNewName] = useState('');
  const [newCat, setNewCat] = useState('mixed');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busy, setBusy] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await createTeam(newName.trim(), newCat);
      toast.success(t('team_created'));
      setNewName('');
      setNewCat('mixed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (team) => {
    setEditId(team.id);
    setEditName(team.name);
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    try {
      await renameTeam(editId, editName.trim());
      toast.success(t('team_updated'));
      setEditId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const handleDelete = async (team) => {
    if (!window.confirm(t('team_delete_confirm').replace('{name}', team.name))) return;
    try {
      await deleteTeam(team.id);
      toast.success(t('team_deleted'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_teams')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl">{t('teams_h1')}</h1>
        <p className="text-gray-400 text-sm mt-2 max-w-2xl">{t('teams_subtitle')}</p>
      </div>

      {/* New team form */}
      <form onSubmit={handleCreate} className="bg-card-steel border border-gold/30 rounded-lg p-5 mb-6" data-testid="team-create-form">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-3">{t('team_create')}</div>
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-end">
          <div className="flex-1 w-full">
            <label className="text-xs text-gray-400 uppercase tracking-wider">{t('team_name')}</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="team-create-name"
              placeholder={t('team_name_placeholder')}
              className="w-full mt-1 bg-navy-deep border border-gold/30 rounded-md px-3 py-2.5 focus:outline-none focus:border-gold text-white"
              maxLength={80}
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1">{t('team_category')}</label>
            <CategoryPicker value={newCat} onChange={setNewCat} />
          </div>
          <button
            type="submit"
            disabled={busy || !newName.trim()}
            data-testid="team-create-submit"
            className="px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light transition disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> {t('team_create')}
          </button>
        </div>
      </form>

      {/* Teams list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && teams.length === 0 && (
          <div className="text-gray-400 text-sm">{t('loading')}…</div>
        )}
        {!loading && teams.length === 0 && (
          <div className="col-span-2 text-center py-12 text-gray-400">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-h uppercase tracking-wider text-lg">{t('teams_empty')}</p>
          </div>
        )}
        {teams.map(team => {
          const isActive = team.id === activeTeamId;
          const isEditing = editId === team.id;
          return (
            <div
              key={team.id}
              data-testid={`team-card-${team.id}`}
              className={`bg-card-steel border-2 rounded-lg p-5 ${isActive ? 'border-gold' : 'border-gold/20'}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                {isEditing ? (
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditId(null); }}
                    autoFocus
                    data-testid={`team-edit-input-${team.id}`}
                    className="flex-1 bg-navy-deep border border-gold/40 rounded-md px-2 py-1.5 text-white font-h uppercase tracking-wider"
                    maxLength={80}
                  />
                ) : (
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Shield className="w-5 h-5 text-gold flex-shrink-0" />
                    <h2 className="font-h uppercase tracking-wider text-lg truncate">{team.name}</h2>
                    {isActive && (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-gold bg-gold/10 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <Star className="w-3 h-3" /> {t('team_active')}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex gap-1.5 flex-shrink-0">
                  {isEditing ? (
                    <>
                      <button onClick={saveEdit} data-testid={`team-edit-save-${team.id}`} className="p-2 rounded-md bg-gold text-navy hover:bg-gold-light">
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditId(null)} className="p-2 rounded-md border border-gold/30 text-gold hover:bg-gold/10">
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(team)} data-testid={`team-edit-${team.id}`} className="p-2 rounded-md border border-gold/30 text-gold hover:bg-gold/10" title={t('edit')}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(team)} data-testid={`team-delete-${team.id}`} className="p-2 rounded-md border border-alert/30 text-alert hover:bg-alert/10" title={t('delete')}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-gray-400 uppercase tracking-wider mr-2">{t('team_category')}</div>
                <CategoryPicker value={team.category || 'mixed'} onChange={(c) => setCategory(team.id, c)} />
              </div>

              <TeamLogoEditor team={team} t={t} />
            </div>
          );
        })}
      </div>
    </Layout>
  );
}

function TeamLogoEditor({ team, t }) {
  const { refresh } = useTeams();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(team.logo_url || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/teams/${team.id}`, { logo_url: draft.trim() || null });
      await refresh();
      toast.success(t('team_logo_saved') || 'Logo actualizado');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-gold/10 pt-3" data-testid={`team-logo-row-${team.id}`}>
      {open ? (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="url"
            placeholder="https://..."
            value={draft}
            onChange={e => setDraft(e.target.value)}
            data-testid={`team-logo-input-${team.id}`}
            className="flex-1 min-w-[200px] bg-navy-deep border border-steel focus:border-gold rounded-md px-3 py-2 text-sm text-white outline-none"
          />
          <button onClick={save} disabled={saving} className="px-3 py-2 rounded-md bg-gold text-navy hover:bg-gold-light text-xs font-h uppercase tracking-wider disabled:opacity-50" data-testid={`team-logo-save-${team.id}`}>
            {t('save')}
          </button>
          <button onClick={() => { setOpen(false); setDraft(team.logo_url || ''); }} className="px-3 py-2 rounded-md border border-gold/30 text-gold text-xs">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {team.logo_url ? (
            <img src={team.logo_url} alt="" className="w-10 h-10 rounded-md object-cover border border-gold/30" />
          ) : (
            <div className="w-10 h-10 rounded-md border border-dashed border-gold/30 flex items-center justify-center text-gold/40">
              <ImageIcon className="w-4 h-4" />
            </div>
          )}
          <button
            onClick={() => setOpen(true)}
            data-testid={`team-logo-edit-${team.id}`}
            className="text-xs font-h uppercase tracking-wider text-gold/80 hover:text-gold"
          >
            {team.logo_url ? (t('change_logo') || 'Cambiar escudo') : (t('add_logo') || 'Añadir escudo')}
          </button>
        </div>
      )}
    </div>
  );
}
